import React, { useState } from 'react';
import { Upload, Database, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { importAllESSProjects, importSingleESSProject, validateESSProject } from '../../utils/importESSProjects';
import { useESSReferenceStore } from '../../store/essReferenceStore';

export const ESSProjectImporter: React.FC = () => {
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const { referenceProjects, sectorBenchmarks, loadReferenceProjects, loadSectorBenchmarks } = useESSReferenceStore();

  const handleImportAll = async () => {
    setIsImporting(true);
    setImportStatus('idle');
    setMessage('');

    try {
      await importAllESSProjects();
      await loadReferenceProjects();
      await loadSectorBenchmarks();
      setImportStatus('success');
      setMessage('Tous les projets ESS ont été importés avec succès !');
    } catch (error: any) {
      setImportStatus('error');
      setMessage(error.message || 'Erreur lors de l\'importation');
    } finally {
      setIsImporting(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportStatus('idle');
    setMessage('');

    try {
      const content = await file.text();
      const data = JSON.parse(content);

      const validation = validateESSProject(data);
      if (!validation.valid) {
        throw new Error(`Fichier invalide: ${validation.errors.join(', ')}`);
      }

      await importSingleESSProject(file);
      await loadReferenceProjects();
      await loadSectorBenchmarks();
      setImportStatus('success');
      setMessage(`Projet "${data.meta.project_name}" importé avec succès !`);
    } catch (error: any) {
      setImportStatus('error');
      setMessage(error.message || 'Erreur lors de l\'importation du fichier');
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Database className="w-6 h-6 text-blue-600" />
        <h2 className="text-xl font-semibold text-gray-900">Import de Projets ESS de Référence</h2>
      </div>

      <div className="space-y-4">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            Importez les projets ESS de référence pour alimenter les benchmarks sectoriels
            et les recommandations du Business Plan IA.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={handleImportAll}
            disabled={isImporting}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isImporting ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>Importation en cours...</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span>Importer tous les projets intégrés</span>
              </>
            )}
          </button>

          <label className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer transition-colors">
            <Upload className="w-5 h-5" />
            <span>Importer un fichier JSON</span>
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              disabled={isImporting}
              className="hidden"
            />
          </label>
        </div>

        {importStatus !== 'idle' && (
          <div
            className={`p-4 rounded-lg border ${
              importStatus === 'success'
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}
          >
            <div className="flex items-start space-x-2">
              {importStatus === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <p
                className={`text-sm ${
                  importStatus === 'success' ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {message}
              </p>
            </div>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Statistiques</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Projets de référence</p>
              <p className="text-2xl font-bold text-gray-900">{referenceProjects.length}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Secteurs avec benchmarks</p>
              <p className="text-2xl font-bold text-gray-900">{sectorBenchmarks.length}</p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Format JSON attendu</h4>
          <div className="text-xs text-gray-600 space-y-1">
            <p>• <strong>meta</strong>: project_id, project_name, category, territory</p>
            <p>• <strong>finance</strong>: capex, funding_plan, pnl, cashflow</p>
            <p>• <strong>impact</strong>: years (avec données annuelles)</p>
            <p>• <strong>governance</strong>: legal_form, policies</p>
            <p>• <strong>kpis</strong>: indicateurs spécifiques au projet</p>
          </div>
        </div>
      </div>
    </div>
  );
};
